import React, { useState, useRef, useCallback, useMemo } from 'react';

export interface GridPoint {
  id: string;
  x: number;
  y: number;
  draggable?: boolean;
  color?: string;
  showLabel?: boolean;
  labelOffset?: { x: number; y: number };
  state?: 'default' | 'correct' | 'incorrect';
}

export interface GridSegment {
  from: string;
  to: string;
  type: 'direct' | 'l-shaped';
  color?: string;
  dashed?: boolean;
  label?: string;
  showHorizontalLabel?: boolean;
  showVerticalLabel?: boolean;
  horizontalLabel?: string;
  verticalLabel?: string;
}

interface CoordinateGridProps {
  size?: number;
  gridRange?: number;
  points?: GridPoint[];
  segments?: GridSegment[];
  disabled?: boolean;
  showLabels?: boolean;
  onPointMove?: (id: string, position: { x: number; y: number }) => void;
  onGridClick?: (position: { x: number; y: number }) => void;
  allowPlacement?: boolean;
}

const CoordinateGrid: React.FC<CoordinateGridProps> = ({
  size = 380,
  gridRange = 5,
  points = [],
  segments = [],
  disabled = false,
  showLabels = true,
  onPointMove,
  onGridClick,
  allowPlacement = false,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draggingPointId, setDraggingPointId] = useState<string | null>(null);
  const [hoveringPointId, setHoveringPointId] = useState<string | null>(null);
  const [localPositions, setLocalPositions] = useState<Record<string, { x: number; y: number }>>({});

  const padding = 50;
  const gridSize = size - 2 * padding;
  const cellSize = gridSize / (gridRange * 2);

  const isDragging = draggingPointId !== null;

  // Grid lines
  const gridLines = useMemo(() => {
    const lines: { index: number; pos: number; isAxis: boolean }[] = [];
    for (let i = 0; i <= gridRange * 2; i++) {
      const pos = padding + i * cellSize;
      const isAxis = i === gridRange;
      lines.push({ index: i, pos, isAxis });
    }
    return lines;
  }, [gridRange, cellSize]);

  // Grid dots (for snapping visual)
  const gridDots = useMemo(() => {
    const dots: { x: number; y: number; px: number; py: number }[] = [];
    for (let x = -gridRange; x <= gridRange; x++) {
      for (let y = -gridRange; y <= gridRange; y++) {
        const px = padding + (x + gridRange) * cellSize;
        const py = padding + (gridRange - y) * cellSize;
        dots.push({ x, y, px, py });
      }
    }
    return dots;
  }, [gridRange, cellSize]);

  // Tick values
  const tickValues = useMemo(() => {
    const ticks: { value: number; pos: number }[] = [];
    for (let i = 0; i <= gridRange * 2; i++) {
      const value = i - gridRange;
      if (value !== 0) {
        const pos = padding + i * cellSize;
        ticks.push({ value, pos });
      }
    }
    return ticks;
  }, [gridRange, cellSize]);

  // Axis arrows
  const axisArrows = useMemo(() => {
    const axisPos = padding + gridRange * cellSize;
    return {
      xArrow: {
        points: `${size - padding + 12},${axisPos} ${size - padding},${axisPos - 6} ${size - padding},${axisPos + 6}`
      },
      yArrow: {
        points: `${axisPos},${padding - 12} ${axisPos - 6},${padding} ${axisPos + 6},${padding}`
      },
      xLabelPos: { x: size - padding + 18, y: axisPos + 6 },
      yLabelPos: { x: axisPos + 12, y: padding - 8 }
    };
  }, [size, gridRange, cellSize]);

  const gridToPixel = useCallback((gridX: number, gridY: number) => {
    const pixelX = padding + (gridX + gridRange) * cellSize;
    const pixelY = padding + (gridRange - gridY) * cellSize;
    return { x: pixelX, y: pixelY };
  }, [gridRange, cellSize]);

  const pixelToGrid = useCallback((pixelX: number, pixelY: number) => {
    const gridX = (pixelX - padding) / cellSize - gridRange;
    const gridY = gridRange - (pixelY - padding) / cellSize;
    return { x: gridX, y: gridY };
  }, [gridRange, cellSize]);

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  const snapToGrid = (value: number) => Math.round(value);

  // Handle click on grid to place a point
  const handleGridClick = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    if (disabled || !allowPlacement || !onGridClick) return;

    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const pixelX = event.clientX - rect.left;
    const pixelY = event.clientY - rect.top;

    // Check if click is within the grid area
    if (pixelX < padding || pixelX > size - padding || pixelY < padding || pixelY > size - padding) {
      return;
    }

    const gridPos = pixelToGrid(pixelX, pixelY);
    const snappedX = snapToGrid(clamp(gridPos.x, -gridRange, gridRange));
    const snappedY = snapToGrid(clamp(gridPos.y, -gridRange, gridRange));

    onGridClick({ x: snappedX, y: snappedY });
  }, [disabled, allowPlacement, onGridClick, pixelToGrid, gridRange, size]);

  const getPointPosition = useCallback((point: GridPoint) => {
    if (point.draggable && localPositions[point.id]) {
      return localPositions[point.id];
    }
    return { x: point.x, y: point.y };
  }, [localPositions]);

  const getPointDisplayPosition = useCallback((point: GridPoint) => {
    const pos = getPointPosition(point);
    return { x: Math.round(pos.x), y: Math.round(pos.y) };
  }, [getPointPosition]);

  const getPointPixelPosition = useCallback((point: GridPoint) => {
    const pos = getPointPosition(point);
    return gridToPixel(pos.x, pos.y);
  }, [getPointPosition, gridToPixel]);

  const handleDragStart = useCallback((event: React.MouseEvent | React.TouchEvent, pointId: string) => {
    if (disabled) return;
    event.preventDefault();
    setDraggingPointId(pointId);

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!svgRef.current) return;
      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();

      let clientX: number, clientY: number;
      if ('touches' in e) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      const pixelX = clientX - rect.left;
      const pixelY = clientY - rect.top;
      const gridPos = pixelToGrid(pixelX, pixelY);

      setLocalPositions(prev => ({
        ...prev,
        [pointId]: {
          x: clamp(gridPos.x, -gridRange, gridRange),
          y: clamp(gridPos.y, -gridRange, gridRange),
        }
      }));
    };

    const handleEnd = () => {
      setLocalPositions(prev => {
        const pos = prev[pointId];
        if (pos) {
          const snappedX = snapToGrid(clamp(pos.x, -gridRange, gridRange));
          const snappedY = snapToGrid(clamp(pos.y, -gridRange, gridRange));
          onPointMove?.(pointId, { x: snappedX, y: snappedY });
          return { ...prev, [pointId]: { x: snappedX, y: snappedY } };
        }
        return prev;
      });
      setDraggingPointId(null);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
  }, [disabled, pixelToGrid, gridRange, onPointMove]);

  const getPointRadius = (point: GridPoint) => {
    const isThisDragging = draggingPointId === point.id;
    if (point.state === 'correct') return 10;
    if (point.state === 'incorrect') return 8;
    if (point.draggable) return isThisDragging ? 18 : 8;
    return 10;
  };

  const getOuterRadius = (point: GridPoint) => {
    const isThisDragging = draggingPointId === point.id;
    if (point.state === 'correct') return 0;
    if (point.state === 'incorrect') return 20;
    if (point.draggable) return isThisDragging ? 28 : 20;
    return 0;
  };

  const showRing = (point: GridPoint) => {
    if (point.state === 'correct') return false;
    if (point.state === 'incorrect') return true;
    if (point.draggable) return true;
    return false;
  };

  const getPointColor = (point: GridPoint) => {
    if (point.state === 'correct') return '#00CD9C';
    if (point.state === 'incorrect') return '#FFC700';
    if (point.draggable) return point.color || '#3B82F6';
    return point.color || '#F97316';
  };

  const getRingColor = () => {
    return '#FFFFFF';
  };

  const getCursor = (point: GridPoint) => {
    if (disabled || point.state) return 'default';
    const isThisDragging = draggingPointId === point.id;
    const isHovering = hoveringPointId === point.id;
    if (isThisDragging) return 'grabbing';
    if (isHovering) return 'grab';
    return 'pointer';
  };

  // Segment calculations
  const getSegmentData = (segment: GridSegment) => {
    const startPoint = points.find(p => p.id === segment.from);
    const endPoint = points.find(p => p.id === segment.to);
    if (!startPoint || !endPoint) return null;

    const startPos = getPointPosition(startPoint);
    const endPos = getPointPosition(endPoint);
    const startPixel = gridToPixel(startPos.x, startPos.y);
    const endPixel = gridToPixel(endPos.x, endPos.y);
    const color = segment.color || '#3B82F6';

    return { startPixel, endPixel, startPos, endPos, color };
  };

  const getHorizontalDistance = (segment: GridSegment) => {
    const data = getSegmentData(segment);
    if (!data) return 0;
    return Math.abs(Math.round(data.endPos.x) - Math.round(data.startPos.x));
  };

  const getVerticalDistance = (segment: GridSegment) => {
    const data = getSegmentData(segment);
    if (!data) return 0;
    return Math.abs(Math.round(data.endPos.y) - Math.round(data.startPos.y));
  };

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={`coordinate-grid ${isDragging ? 'dragging' : ''}`}
      style={{
        backgroundColor: '#E8F4F8',
        borderRadius: 8,
        touchAction: 'none',
        userSelect: 'none',
        cursor: allowPlacement && !disabled ? 'crosshair' : 'default',
      }}
      onClick={handleGridClick}
    >
      {/* Grid lines */}
      {gridLines.map((line) => (
        <React.Fragment key={line.index}>
          <line
            x1={line.pos}
            y1={padding}
            x2={line.pos}
            y2={size - padding}
            stroke={line.isAxis ? '#1E293B' : '#CBD5E1'}
            strokeWidth={line.isAxis ? 2 : 1}
            opacity={isDragging ? 1 : 0.5}
          />
          <line
            x1={padding}
            y1={line.pos}
            x2={size - padding}
            y2={line.pos}
            stroke={line.isAxis ? '#1E293B' : '#CBD5E1'}
            strokeWidth={line.isAxis ? 2 : 1}
            opacity={isDragging ? 1 : 0.5}
          />
        </React.Fragment>
      ))}

      {/* Axis arrows */}
      <polygon points={axisArrows.xArrow.points} fill="#1E293B" />
      <polygon points={axisArrows.yArrow.points} fill="#1E293B" />

      {/* Axis labels */}
      <text
        x={axisArrows.xLabelPos.x}
        y={axisArrows.xLabelPos.y}
        fontSize={18}
        fontWeight={600}
        fontStyle="italic"
        fill="#1E293B"
      >x</text>
      <text
        x={axisArrows.yLabelPos.x}
        y={axisArrows.yLabelPos.y}
        fontSize={18}
        fontWeight={600}
        fontStyle="italic"
        fill="#1E293B"
      >y</text>

      {/* Tick marks */}
      {tickValues.map((tick) => (
        <React.Fragment key={tick.value}>
          <line
            x1={tick.pos}
            y1={padding + gridRange * cellSize - 5}
            x2={tick.pos}
            y2={padding + gridRange * cellSize + 5}
            stroke="#1E293B"
            strokeWidth={1.5}
          />
          <line
            x1={padding + gridRange * cellSize - 5}
            y1={tick.pos}
            x2={padding + gridRange * cellSize + 5}
            y2={tick.pos}
            stroke="#1E293B"
            strokeWidth={1.5}
          />
        </React.Fragment>
      ))}

      {/* Grid dots (visible when dragging) */}
      {gridDots.map((dot) => (
        <circle
          key={`${dot.x}-${dot.y}`}
          cx={dot.px}
          cy={dot.py}
          r={3}
          fill="#CBD5E1"
          opacity={isDragging ? 0.6 : 0}
          style={{ transition: 'opacity 0.2s ease' }}
        />
      ))}

      {/* Segments */}
      {segments.map((segment, index) => {
        const data = getSegmentData(segment);
        if (!data) return null;

        if (segment.type === 'l-shaped') {
          return (
            <g key={index}>
              {/* Horizontal line */}
              <line
                x1={data.startPixel.x}
                y1={data.startPixel.y}
                x2={data.endPixel.x}
                y2={data.startPixel.y}
                stroke={data.color}
                strokeWidth={3}
                strokeLinecap="round"
              />
              {/* Vertical line */}
              <line
                x1={data.endPixel.x}
                y1={data.startPixel.y}
                x2={data.endPixel.x}
                y2={data.endPixel.y}
                stroke={data.color}
                strokeWidth={3}
                strokeLinecap="round"
              />

              {/* Horizontal distance label */}
              {segment.showHorizontalLabel && (
                <>
                  <line
                    x1={data.startPixel.x}
                    y1={data.startPixel.y + 20}
                    x2={data.endPixel.x}
                    y2={data.startPixel.y + 20}
                    stroke="#CBD5E1"
                    strokeWidth={1}
                  />
                  <text
                    x={(data.startPixel.x + data.endPixel.x) / 2}
                    y={data.startPixel.y + 38}
                    textAnchor="middle"
                    fontSize={16}
                    fontWeight={500}
                    fill="#64748B"
                  >
                    {segment.horizontalLabel || getHorizontalDistance(segment)}
                  </text>
                </>
              )}

              {/* Vertical distance label */}
              {segment.showVerticalLabel && (
                <>
                  <line
                    x1={data.startPixel.x - 20}
                    y1={data.startPixel.y}
                    x2={data.startPixel.x - 20}
                    y2={data.endPixel.y}
                    stroke="#CBD5E1"
                    strokeWidth={1}
                  />
                  <text
                    x={data.startPixel.x - 35}
                    y={(data.startPixel.y + data.endPixel.y) / 2 + 5}
                    textAnchor="middle"
                    fontSize={16}
                    fontWeight={500}
                    fill="#64748B"
                  >
                    {segment.verticalLabel || getVerticalDistance(segment)}
                  </text>
                </>
              )}
            </g>
          );
        }

        if (segment.type === 'direct') {
          return (
            <g key={index}>
              <line
                x1={data.startPixel.x}
                y1={data.startPixel.y}
                x2={data.endPixel.x}
                y2={data.endPixel.y}
                stroke={segment.dashed ? '#CBD5E1' : data.color}
                strokeWidth={segment.dashed ? 2 : 3}
                strokeLinecap="round"
                strokeDasharray={segment.dashed ? '6,4' : 'none'}
              />
              {segment.label && (
                <text
                  x={(data.startPixel.x + data.endPixel.x) / 2 + 15}
                  y={(data.startPixel.y + data.endPixel.y) / 2}
                  textAnchor="middle"
                  fontSize={18}
                  fontWeight={500}
                  fill="#64748B"
                >
                  {segment.label}
                </text>
              )}
            </g>
          );
        }

        return null;
      })}

      {/* Points */}
      {points.map((point) => {
        const pixelPos = getPointPixelPosition(point);
        const displayPos = getPointDisplayPosition(point);

        return (
          <g key={point.id}>
            {/* Outer ring */}
            {showRing(point) && (
              <circle
                cx={pixelPos.x}
                cy={pixelPos.y}
                r={getOuterRadius(point)}
                fill={getRingColor()}
                style={{
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
                  transition: draggingPointId === point.id ? 'none' : 'r 0.2s ease',
                }}
              />
            )}

            {/* Inner circle */}
            <circle
              cx={pixelPos.x}
              cy={pixelPos.y}
              r={getPointRadius(point)}
              fill={getPointColor(point)}
              style={{ transition: draggingPointId === point.id ? 'none' : 'r 0.2s ease' }}
            />

            {/* Draggable interaction layer */}
            {point.draggable && !point.state && (
              <circle
                cx={pixelPos.x}
                cy={pixelPos.y}
                r={30}
                fill="transparent"
                style={{ cursor: getCursor(point) }}
                onMouseDown={(e) => handleDragStart(e, point.id)}
                onTouchStart={(e) => handleDragStart(e, point.id)}
                onMouseEnter={() => setHoveringPointId(point.id)}
                onMouseLeave={() => setHoveringPointId(null)}
              />
            )}

            {/* Coordinate label */}
            {point.showLabel && showLabels && (
              <text
                x={pixelPos.x + (point.labelOffset?.x || 15)}
                y={pixelPos.y + (point.labelOffset?.y || -15)}
                fontSize={16}
                fontWeight={500}
                fill="#1E293B"
              >
                ({displayPos.x}, {displayPos.y})
              </text>
            )}

            {/* Correct badge */}
            {point.state === 'correct' && (
              <g transform={`translate(${pixelPos.x - 12}, ${pixelPos.y - 45})`}>
                <rect x={0} y={0} width={24} height={24} rx={6} fill="#00CD9C" />
                <path
                  d="M6 12l4 4 8-8"
                  stroke="white"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </g>
            )}

            {/* Incorrect badge */}
            {point.state === 'incorrect' && (
              <g transform={`translate(${pixelPos.x - 12}, ${pixelPos.y - 45})`}>
                <rect x={0} y={0} width={24} height={24} rx={6} fill="#FFC700" />
                <path
                  d="M7 7l10 10M17 7l-10 10"
                  stroke="white"
                  strokeWidth={3}
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default CoordinateGrid;
